import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

function TermsOfService() {
    return (
        <div className="legal-page">
            <header className="page-section-head">
                <h1>Legal <span className="page-section-current">Terms of Service</span></h1>
            </header>
            <div className="legal-container">
                <p className="legal-updated">Effective Date: July 12, 2026</p>

                <section>
                    <p>
                        Welcome to Trade Spot. These Terms of Service ("Terms") govern your access to and use of
                        the Trade Spot platform, website, and services (collectively, the "Service"). By accessing
                        or using the Service, you agree to be bound by these Terms. If you do not agree to these
                        Terms, you may not use the Service.
                    </p>
                    <p>
                        Please also review our <Link to="/privacy">Privacy Policy</Link>, which describes how we
                        collect and use your information, and our <Link to="/credits">Credits & Legal</Link> page,
                        which contains important disclaimers regarding third-party content.
                    </p>
                </section>

                <section>
                    <h2>1. Eligibility</h2>
                    <p>
                        You must be at least 13 years of age to use the Service. If you are under 18, you represent
                        that you have your parent or guardian's permission to use the Service. By using the Service,
                        you represent and warrant that you meet these eligibility requirements.
                    </p>
                </section>

                <section>
                    <h2>2. Account Registration</h2>
                    <p>To access certain features of the Service, you must create an account. When creating an account, you agree to:</p>
                    <ul>
                        <li>Provide accurate, current, and complete information</li>
                        <li>Maintain and promptly update your account information</li>
                        <li>Maintain the security and confidentiality of your login credentials</li>
                        <li>Accept responsibility for all activities that occur under your account</li>
                        <li>Notify us immediately of any unauthorized use of your account</li>
                    </ul>
                    <p>
                        You may not use another person's account without permission. We reserve the right to suspend
                        or terminate accounts that violate these Terms.
                    </p>
                </section>

                <section>
                    <h2>3. The Service</h2>
                    <p>
                        Trade Spot is a platform that allows users to manage their trading card collections, create
                        wishlists, and facilitate trades with other users. The Service includes:
                    </p>
                    <ul>
                        <li>Card collection management (tracking cards you have and want)</li>
                        <li>Trade matching and discovery features</li>
                        <li>A structured trade and shipping workflow (see Section 5)</li>
                        <li>An optional middleman program operated by independent card shops (see Section 6)</li>
                        <li>Card search and database functionality</li>
                    </ul>
                    <p>
                        Trade Spot is a venue for connecting users. Trade Spot itself does not take possession
                        of, verify, authenticate, buy, sell, or ship any physical trading cards. All physical
                        transactions are conducted directly between users, or between users and independent
                        middleman shops.
                    </p>
                </section>

                <section>
                    <h2>4. User Conduct</h2>
                    <p>When using the Service, you agree NOT to:</p>
                    <ul>
                        <li>Violate any applicable laws, regulations, or third-party rights</li>
                        <li>Post false, misleading, or fraudulent information about your collection or trade offers</li>
                        <li>Attempt to scam, defraud, or deceive other users</li>
                        <li>Harass, threaten, or abuse other users</li>
                        <li>Use the Service for any illegal purpose, including selling counterfeit cards</li>
                        <li>Interfere with or disrupt the Service or servers</li>
                        <li>Attempt to gain unauthorized access to any part of the Service</li>
                        <li>Use automated systems (bots, scrapers) without our permission</li>
                        <li>Impersonate another person or entity</li>
                        <li>Collect or harvest user information without consent</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Trading Guidelines</h2>
                    <p>
                        Trade Spot facilitates connections between users who wish to trade cards.
                        By using our trading features, you acknowledge and agree that:
                    </p>
                    <ul>
                        <li>
                            <strong>Accuracy:</strong> You are responsible for accurately representing the cards
                            in your collection, including their condition, authenticity, and availability.
                        </li>
                        <li>
                            <strong>Good Faith:</strong> You will engage in trades in good faith and honor
                            agreed-upon trades.
                        </li>
                        <li>
                            <strong>Risk:</strong> All trades are conducted at your own risk. Trade Spot is not
                            responsible for the quality, authenticity, safety, or legality of items traded.
                        </li>
                        <li>
                            <strong>Disputes:</strong> Trade Spot does not mediate disputes between users.
                            You are responsible for resolving any issues directly with your trading partners.
                        </li>
                        <li>
                            <strong>Third-Party Services:</strong> If you use third-party services (such as card
                            shops or shipping services) to complete trades, you are bound by their terms and
                            responsible for any fees they charge.
                        </li>
                        <li>
                            <strong>Locked-In Steps:</strong> The trade workflow contains explicit accept steps
                            (the middleman decision, the shipping setup review, and the card documentation
                            review). Accepting a step is FINAL and cannot be reversed. Denying a step, or
                            dropping out before shipping begins, cancels the entire trade for both parties.
                        </li>
                        <li>
                            <strong>Documentation:</strong> For direct (no-middleman) trades, each party must
                            photograph and describe the cards they are sending, and photograph their sealed
                            package, before shipping unlocks. These records exist to protect both parties.
                            Submitting false or misleading documentation is a violation of these Terms.
                        </li>
                        <li>
                            <strong>Shipping Costs:</strong> Each party bears their own shipping costs and any
                            middleman fees applicable to their side of the trade unless agreed otherwise.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>6. Middleman Program</h2>
                    <p>
                        Verified card shops ("Middlemen") may act as intermediaries for trades. When either
                        party requests a middleman, both parties ship their cards to their selected shops,
                        which inspect the contents against the trade before forwarding them. By using the
                        middleman program you acknowledge that:
                    </p>
                    <ul>
                        <li>
                            Middlemen are <strong>independent businesses</strong>, not employees, agents, or
                            representatives of Trade Spot. Verification means an administrator approved their
                            account; it is not a guarantee of their performance.
                        </li>
                        <li>
                            Middlemen set and collect their own service fees, published on their store pages.
                            Those fees are strictly between you and the shop.
                        </li>
                        <li>
                            A middleman that determines a package does not match the trade may reject it,
                            which cancels the whole trade. Return of rejected cards is arranged between you
                            and the shop holding them.
                        </li>
                        <li>
                            Cards are never forwarded until every middleman involved has verified their side
                            of the trade.
                        </li>
                        <li>
                            Middleman reviews are limited to one per trade, are final once submitted, and
                            must reflect a genuine experience.
                        </li>
                        <li>
                            Trade Spot is not liable for loss, damage, theft, or misdelivery of cards while
                            they are in a middleman&apos;s custody or in transit.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>7. Fees</h2>
                    <p>
                        Trading on Trade Spot is currently free for individual users. Shop (middleman)
                        accounts may be subject to fees, including an application processing fee and a
                        recurring account fee, which will be communicated before they are charged. Except
                        where required by law, fees are non-refundable. Middlemen&apos;s own service fees are
                        set by and paid to the shops directly and are not collected by Trade Spot.
                    </p>
                </section>

                <section>
                    <h2>8. Intellectual Property</h2>

                    <h3>8.1 Trade Spot Content</h3>
                    <p>
                        The Service, including its design, features, and original content, is owned by Trade Spot
                        and protected by copyright and other intellectual property laws. You may not copy, modify,
                        distribute, or create derivative works without our permission.
                    </p>

                    <h3>8.2 Third-Party Content</h3>
                    <p>
                        Trade Spot displays card data and images from various trading card games. This content
                        belongs to the respective game publishers and is used in accordance with their fan content
                        policies. For complete information about third-party content usage and disclaimers,
                        please see our <Link to="/credits">Credits & Legal</Link> page.
                    </p>

                    <h3>8.3 User Content</h3>
                    <p>
                        You retain ownership of any content you submit to the Service (such as profile information
                        and collection data). By submitting content, you grant Trade Spot a non-exclusive,
                        royalty-free license to use, display, and store that content as necessary to provide the Service.
                    </p>
                </section>

                <section>
                    <h2>9. Disclaimers</h2>
                    <p>
                        <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                            EITHER EXPRESS OR IMPLIED.</strong> We do not warrant that:
                    </p>
                    <ul>
                        <li>The Service will be uninterrupted, secure, or error-free</li>
                        <li>Card data or pricing information will be accurate or up-to-date</li>
                        <li>Any trades you conduct will be successful or satisfactory</li>
                        <li>Other users will act in good faith or honor their commitments</li>
                    </ul>
                    <p>
                        Trade Spot is not affiliated with, endorsed by, or sponsored by any trading card game
                        publisher. See our <Link to="/credits">Credits & Legal</Link> page for specific disclaimers.
                    </p>
                </section>

                <section>
                    <h2>10. Limitation of Liability</h2>
                    <p>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRADE SPOT AND ITS OPERATORS SHALL NOT BE LIABLE
                        FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT
                        NOT LIMITED TO:
                    </p>
                    <ul>
                        <li>Loss of profits, data, or goodwill</li>
                        <li>Losses arising from trades with other users</li>
                        <li>Counterfeit, damaged, or misrepresented cards</li>
                        <li>Failed or incomplete trades</li>
                        <li>Actions or inactions of third-party services</li>
                        <li>Unauthorized access to your account</li>
                    </ul>
                    <p>
                        Our total liability for any claims arising from your use of the Service shall not exceed
                        the amount you paid to Trade Spot in the twelve (12) months preceding the claim, or $100,
                        whichever is less.
                    </p>
                </section>

                <section>
                    <h2>11. Indemnification</h2>
                    <p>
                        You agree to indemnify and hold harmless Trade Spot, its operators, and affiliates from
                        any claims, damages, losses, or expenses (including legal fees) arising from:
                    </p>
                    <ul>
                        <li>Your use of the Service</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any third-party rights</li>
                        <li>Disputes with other users</li>
                        <li>Content you submit to the Service</li>
                    </ul>
                </section>

                <section>
                    <h2>12. Termination</h2>
                    <p>
                        We reserve the right to suspend or terminate your account and access to the Service at
                        any time, for any reason, including but not limited to violation of these Terms. You may
                        also delete your account at any time through your account settings.
                    </p>
                    <p>
                        Deleting your account anonymizes your profile and removes your collections. Records
                        of completed trades are retained (in anonymized form) so the other participants&apos;
                        trade histories remain intact. See the <Link to="/privacy">Privacy Policy</Link> for details.
                    </p>
                    <p>
                        Upon termination, your right to use the Service will immediately cease. Sections of these
                        Terms that by their nature should survive termination will remain in effect.
                    </p>
                </section>

                <section>
                    <h2>13. Changes to Terms</h2>
                    <p>
                        We may modify these Terms at any time. If we make material changes, we will notify you
                        by updating the "Effective Date" at the top of these Terms and, where appropriate,
                        provide additional notice. Your continued use of the Service after changes take effect
                        constitutes acceptance of the modified Terms.
                    </p>
                </section>

                <section>
                    <h2>14. Governing Law</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of the
                        jurisdiction in which Trade Spot operates, without regard to conflict of law principles.
                    </p>
                </section>

                <section>
                    <h2>15. Dispute Resolution</h2>
                    <p>
                        Any disputes arising from these Terms or your use of the Service shall first be attempted
                        to be resolved through informal negotiation. If informal resolution is unsuccessful,
                        disputes shall be resolved through binding arbitration or small claims court, at our discretion.
                    </p>
                </section>

                <section>
                    <h2>16. Severability</h2>
                    <p>
                        If any provision of these Terms is found to be unenforceable or invalid, that provision
                        shall be limited or eliminated to the minimum extent necessary, and the remaining provisions
                        shall remain in full force and effect.
                    </p>
                </section>

                <section>
                    <h2>17. Contact Information</h2>
                    <p>For questions about these Terms, please contact us at:</p>
                    <p>
                        <strong>Trade Spot</strong><br />
                        Email: <a href="mailto:support@thetraderspot.com">support@thetraderspot.com</a>
                    </p>
                </section>

                <section className="legal-acknowledgment">
                    <p>
                        <em>
                            By using Trade Spot, you acknowledge that you have read, understood, and agree to be
                            bound by these Terms of Service.
                        </em>
                    </p>
                </section>
            </div>
        </div>
    );
}

export default TermsOfService;

